import { ReactElement, ReactNode } from 'react';
import classnames from 'classnames';
import StarIcon from '../../assets/icons/StarIcon';

export type Variant = 1 | 2 | 3 | 4 | 5 | 6;

interface Props {
  title: string | ReactNode;
  description: string | ReactNode;
  footer?: ReactNode;
  className?: string;
  onClick?: () => void;
  variant: Variant;
}

const variantBg: Record<NonNullable<Props['variant']>, string> = {
  1: 'bg-gradient-to-tr from-orange-400 to-red-500',
  2: 'bg-gradient-to-r from-pink-500 to-purple-600 dark:from-purple-800 dark:to-slate-700',
  3: 'bg-gradient-to-r from-cyan-400 to-teal-500 dark:from-emerald-700 dark:to-teal-800',
  4: 'bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-indigo-800 dark:to-purple-700',
  5: 'bg-gradient-to-r from-orange-500 to-red-500 dark:from-amber-700 dark:to-red-800',
  6: 'bg-gradient-to-r from-green-500 to-lime-500 dark:from-emerald-800 dark:to-green-700',
};

const bubbles: Record<NonNullable<Props['variant']>, { b1: string; b2: string }> = {
  1: {
    b1: 'absolute -right-5 -top-5 w-20 h-20 bg-white/20 rounded-full',
    b2: 'absolute -right-8 -bottom-4 w-16 h-16 bg-white/10 rounded-full',
  },
  2: {
    b1: 'absolute -right-2 -top-2 w-16 h-16 bg-white/20 rounded-full',
    b2: 'absolute -right-8 -bottom-4 w-20 h-20 bg-white/10 rounded-full',
  },
  3: {
    b1: 'absolute -right-4 -top-4 w-20 h-20 bg-white/20 rounded-full',
    b2: 'absolute -right-12 -bottom-2 w-16 h-16 bg-white/10 rounded-full',
  },
  4: {
    b1: 'absolute -right-6 -top-6 w-24 h-24 bg-white/20 rounded-full',
    b2: 'absolute -right-2 -bottom-8 w-18 h-18 bg-white/10 rounded-full',
  },
  5: {
    b1: 'absolute -right-3 -top-3 w-18 h-18 bg-white/20 rounded-full',
    b2: 'absolute -right-10 -bottom-6 w-22 h-22 bg-white/10 rounded-full',
  },
  6: {
    b1: 'absolute -right-5 -top-5 w-20 h-20 bg-white/20 rounded-full',
    b2: 'absolute -right-8 -bottom-4 w-16 h-16 bg-white/10 rounded-full',
  },
} as const;

export default function DiscountCard(props: Props): ReactElement {
  const { title, description, className, onClick } = props;

  return (
    <div
      className={classnames(
        'h-full rounded-2xl p-4 flex items-center justify-between',
        'relative overflow-hidden shadow-lg cursor-pointer',
        variantBg[props.variant],
        className,
      )}
      onClick={onClick}
    >
      <div className="z-10 h-full space-y-2 flex flex-col justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-white font-semibold text-md leading-tight">{title}</div>
          {description ? <div className="text-xs text-white/80">{description}</div> : null}
        </div>
        <Footer {...props} />
      </div>

      <div className="z-10">
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
          <StarIcon className="w-6 h-6 text-white" />
        </div>
      </div>

      <div className={bubbles[props.variant].b1} />
      <div className={bubbles[props.variant].b2} />
    </div>
  );
}

const buttonColors: Record<NonNullable<Props['variant']>, string> = {
  1: 'bg-white text-orange-400 hover:bg-white/90 dark:text-orange-800',
  2: 'bg-white text-purple-600 hover:bg-white/90 dark:text-purple-800',
  3: 'bg-white text-teal-600 hover:bg-white/90 dark:text-teal-800',
  4: 'bg-white text-blue-600 hover:bg-white/90 dark:text-purple-800',
  5: 'bg-white text-orange-600 hover:bg-white/90 dark:text-orange-800',
  6: 'bg-white text-green-600 hover:bg-white/90 dark:text-emerald-800',
};

const Footer = ({ footer, variant }: Props) => {
  if (!footer) {
    return null;
  }

  return (
    <button
      className={classnames(
        'font-normal text-sm mt-sm min-w-[116px] w-fit rounded-xl py-2 px-3 hover:scale-[1.02] active:scale-[0.98] transform transition-all duration-200',
        buttonColors[variant],
      )}
    >
      {footer}
    </button>
  );
};
